/**
 * Formattazione di date e orari.
 *
 * Gli istanti viaggiano e vengono salvati in UTC; la resa in ora locale avviene
 * solo qui, sempre con un fuso esplicito. Un evento di cronometraggio si legge
 * in ora italiana anche se il server gira altrove, quindi il fuso è una
 * configurazione (`TZ_NAME`) e non il fuso del processo.
 */
export const DEFAULT_TIME_ZONE = 'Europe/Rome';

export type DateInput = string | number | Date;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/** `02/10/2026, 14:32` */
export function formatDateTimeIt(value: DateInput, timeZone = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('it-IT', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(value));
}

/** `02/10/2026` */
export function formatDateIt(value: DateInput, timeZone = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('it-IT', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(toDate(value));
}

/** `14:32` */
export function formatTimeIt(value: DateInput, timeZone = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('it-IT', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(value));
}

/** `2026-10-02` — per nomi di file e valori `<input type="date">`. */
export function formatDateIso(value: DateInput, timeZone = DEFAULT_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(toDate(value));

  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Converte un istante in una data "da calendario" per Excel.
 *
 * Excel non conosce i fusi: una cella data è un numero puro, e il programma la
 * mostra così com'è. Scrivendo direttamente l'istante UTC, un rientro delle
 * 14:32 italiane comparirebbe come 12:32. Questa funzione costruisce quindi una
 * data i cui componenti UTC *sono* l'ora locale del fuso richiesto, che è
 * esattamente ciò che Excel si aspetta di ricevere.
 */
export function toExcelDate(value: DateInput, timeZone = DEFAULT_TIME_ZONE): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(toDate(value));

  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');

  return new Date(
    Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')),
  );
}

/** `02/10/2026` a partire da una data ISO `AAAA-MM-GG`, senza passare dai fusi. */
export function formatIsoDateIt(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

/** Percentuale in forma italiana: `0,678` → `67,8%`. */
export function formatPercentIt(fraction: number, digits = 1): string {
  return `${(fraction * 100).toFixed(digits).replace('.', ',')}%`;
}
